import { createMachine } from 'xstate';
import { routes as getRoutes } from 'config';
import { actions } from './actions';
import { guards } from './guards';
import * as Screens from '../screens';
import { ApolloServiceHandler } from './apollo-service-handler';

const routes = getRoutes();
export const states = {
  selectUtilityAccount: 'selectUtilityAccount',
  addMovingDetails: 'addMovingDetails',
  checkUtility: 'checkUtility',
  addUtilityServiceDetails: 'addUtilityServiceDetails',
  setUpService: 'setUpService',
  getSetupReminder: 'getSetupReminder',
  addOnlineUtilityAccount: 'addOnlineUtilityAccount',
  getOnlineSetupReminder: 'getOnlineSetupReminder',
  viewConfirmation: 'viewConfirmation',
  addPaymentMethod: 'addPaymentMethod',
  reviewNewHome: 'reviewNewHome',
  viewSuccess: 'viewSuccess',
  connectUtility: 'connectUtility',
};
export const events = {
  previous: 'previous',
  next: 'next',
  hasSetUpService: 'hasSetUpService',
  hasNotSetUpService: 'hasNotSetUpService',
  hasServiceOnline: 'hasServiceOnline',
  doesNotHaveServiceOnline: 'doesNotHaveServiceOnline',
};

const config: MovingStateMachineConfig = {
  id: 'moving',
  initial: states.selectUtilityAccount,
  context: {
    apolloClient: undefined,
    history: undefined,
    errors: {},
    selectedUtilityAccount: undefined,
    zipCode: '',
    utility: undefined,
    account: undefined,
    userId: undefined,
    accountId: undefined,
    newPlanAmountCents: undefined,
    newPlanId: undefined,
    requiresPaymentMethod: undefined,
    paymentMethodId: undefined,
    newUtilityAccountId: undefined,
    utilityAccountBillingExperience: undefined,
    billingExperience: undefined,
    enrollmentOptions: undefined,
  },
  states: {
    [states.selectUtilityAccount]: {
      id: 'selectUtilityAccount',
      initial: 'loadAccount',
      meta: {
        previousRoute: routes.settings,
        route: routes.movingSelectAccount,
        Component: Screens.SelectUtilityAccount,
      },
      on: {
        [events.previous]: {
          actions: 'goToSettings',
        },
        [events.next]: {
          actions: [
            'setSelectedPlan',
            'setSelectedUtilityAccount',
            'setMoveRequest',
            'trackSelectedUtilityAccount',
          ],
          cond: 'selectedUtilityAccountIsSet',
          target: states.addMovingDetails,
        },
      },
      states: {
        loadAccount: {
          invoke: {
            src: 'getAccountInfo',
            onDone: {
              actions: 'setAccount',
              target: 'accountLoaded',
            },
            onError: {
              target: 'accountLoadingError',
            },
          },
        },
        accountLoaded: { type: 'final' },
        accountLoadingError: {
          meta: {
            previousRoute: routes.settings,
            route: routes.movingSelectAccount,
            Component: Screens.SelectUtilityAccount,
          },
        },
      },
    },
    [states.addMovingDetails]: {
      id: 'addMovingDetails',
      initial: 'idle',
      meta: {
        previousRoute: routes.movingSelectAccount,
        route: routes.movingAddAccountDetails,
        Component: Screens.AddMovingDetails,
      },
      on: {
        [events.previous]: 'selectUtilityAccount',
      },
      states: {
        idle: {
          on: {
            [events.next]: {
              actions: [
                'setNewAccountAttributes',
                'trackNewAccountAttributesCreated',
              ],
              cond: 'zipCodeAndUtilityAreSet',
              target: 'processMoveRequest',
            },
          },
        },
        processMoveRequest: {
          invoke: {
            src: 'createOrUpdateMoveRequest',
            onDone: {
              actions: ['setMoveRequest', 'trackMoveRequestCreated'],
              target: 'fetchUtilityAccountsBillingExperience',
            },
            onError: {
              actions: 'setCreateOrUpdateMoveRequestError',
              target: 'idle',
            },
          },
        },
        fetchUtilityAccountsBillingExperience: {
          invoke: {
            src: 'fetchUtilityAccountsBillingExperience',
            onDone: {
              actions: 'setUtilityAccountBillingExperience',
              target: 'fetchBillingExperienceOptions',
            },
            onError: {
              target: 'fetchUtilityAccountBillingExperienceOptionsError',
            },
          },
        },
        fetchBillingExperienceOptions: {
          invoke: {
            src: 'fetchBillingExperienceOptions',
            onDone: {
              actions: 'setBillingExperience',
              target: 'getPlanTemplateData',
            },
            onError: {
              target: 'fetchBillingExperienceOptionsError',
            },
          },
        },
        getPlanTemplateData: {
          invoke: {
            src: 'getPlanTemplateData',
            onDone: {
              actions: 'setRequiresPaymentMethod',
              target: 'moveRequestProcessed',
            },
            onError: {
              target: 'getPlanTemplateDataError',
            },
          },
        },
        getPlanTemplateDataError: { type: 'final' },
        fetchUtilityAccountBillingExperienceOptionsError: { type: 'final' },
        fetchBillingExperienceOptionsError: { type: 'final' },
        moveRequestProcessed: { type: 'final' },
      },
      onDone: [
        {
          cond: 'shouldUseSameUtilityFlow',
          actions: 'trackUsingSameUtilityFlow',
          target: states.addUtilityServiceDetails,
        },
        {
          cond: 'shouldAddPaymentMethod',
          target: states.addPaymentMethod,
        },
        { target: states.reviewNewHome },
      ],
    },
    [states.addUtilityServiceDetails]: {
      id: 'addUtilityServiceDetails',
      initial: 'idle',
      meta: {
        previousRoute: routes.movingAddAccountDetails,
        route: routes.movingAddUtilityServiceDetails,
        Component: Screens.AddUtilityServiceDetails,
      },
      on: {
        [events.previous]: states.addMovingDetails,
      },
      states: {
        idle: {
          on: {
            [events.hasSetUpService]: 'serviceSetUp',
            [events.hasNotSetUpService]: 'serviceNotSetUp',
          },
        },
        serviceSetUp: {
          initial: 'idle',
          on: {
            [events.hasNotSetUpService]: 'serviceNotSetUp',
            [events.doesNotHaveServiceOnline]: 'serviceSetUp.serviceNotOnline',
            [events.hasServiceOnline]: 'serviceSetUp.serviceOnline',
          },
          states: {
            idle: {
              on: {
                [events.next]: '#viewConfirmation',
              },
            },
            serviceOnline: {
              actions: 'trackServiceOnline',
              on: {
                [events.next]: '#viewConfirmation',
              },
            },
            serviceNotOnline: {
              actions: 'trackServiceNotOnline',
              on: {
                [events.next]: '#addOnlineUtilityAccount',
              },
            },
          },
        },
        serviceNotSetUp: {
          on: {
            [events.hasSetUpService]: 'serviceSetUp',
            [events.next]: '#setUpService',
          },
        },
      },
    },
    [states.setUpService]: {
      id: 'setUpService',
      meta: {
        previousRoute: routes.movingAddUtilityServiceDetails,
        route: routes.movingSetUpService,
        Component: Screens.SetUpService,
      },
      on: {
        [events.previous]: 'addUtilityServiceDetails.serviceNotSetUp',
        [events.hasSetUpService]: 'addUtilityServiceDetails.serviceSetUp',
        [events.next]: states.getSetupReminder,
      },
    },
    [states.getSetupReminder]: {
      meta: {
        previousRoute: routes.movingSetUpService,
        route: routes.movingGetSetupReminder,
        Component: Screens.ViewConfirmation,
      },
      on: {
        [events.previous]: states.setUpService,
      },
    },
    [states.addOnlineUtilityAccount]: {
      id: 'addOnlineUtilityAccount',
      meta: {
        previousRoute: routes.movingAddUtilityServiceDetails,
        route: routes.movingAddOnlineAccount,
        Component: Screens.AddOnlineUtilityAccount,
      },
      on: {
        [events.previous]:
          'addUtilityServiceDetails.serviceSetUp.serviceNotOnline',
        [events.next]: states.getOnlineSetupReminder,
      },
    },
    [states.getOnlineSetupReminder]: {
      meta: {
        previousRoute: routes.movingAddOnlineAccount,
        route: routes.movingGetOnlineSetupReminder,
        Component: Screens.ViewConfirmation,
      },
      on: {
        [events.previous]: states.addOnlineUtilityAccount,
      },
    },
    [states.viewConfirmation]: {
      id: 'viewConfirmation',
      meta: {
        previousRoute: routes.movingAddUtilityServiceDetails,
        route: routes.movingViewConfirmation,
        Component: Screens.ViewConfirmation,
      },
      on: {
        [events.previous]:
          'addUtilityServiceDetails.serviceSetUp.serviceOnline',
      },
    },
    [states.addPaymentMethod]: {
      initial: 'loadNewPlanAmountCents',
      meta: {
        previousRoute: routes.movingAddAccountDetails,
        route: routes.movingAddPaymentMethod,
        Component: Screens.AddPaymentMethod,
      },
      on: {
        [events.previous]: states.addMovingDetails,
      },
      states: {
        loadNewPlanAmountCents: {
          invoke: {
            src: 'getNewPlanAmountCents',
            onDone: {
              actions: ['setNewPlanAmountCents', 'trackAddPaymentMethod'],
              target: 'createSetupIntent',
            },
            onError: {
              target: 'loadingError',
            },
          },
        },
        createSetupIntent: {
          invoke: {
            src: 'createSetupIntent',
            onDone: {
              actions: 'setStripeClientSecret',
              target: 'fetchExistingPaymentMethodIds',
            },
            onError: {
              target: 'loadingError',
            },
          },
        },
        fetchExistingPaymentMethodIds: {
          invoke: {
            src: 'fetchExistingPaymentMethodIds',
            onDone: {
              actions: 'setExistingPaymentMethodIds',
              target: 'idle',
            },
            onError: {
              target: 'loadingError',
            },
          },
        },
        idle: {
          on: {
            [events.next]: 'pollForPaymentCreation',
          },
        },
        pollForPaymentCreation: {
          invoke: {
            src: 'pollForPaymentCreation',
            onDone: {
              actions: ['setPaymentMethodId', 'trackPaymentMethodCreated'],
              target: '#reviewNewHome',
            },
            onError: {
              target: 'pollingError',
            },
          },
        },
        loadingError: { type: 'final' },
        pollingError: { type: 'final' },
      },
    },
    [states.reviewNewHome]: {
      id: 'reviewNewHome',
      initial: 'loadNewPlanAmountCents',
      meta: {
        previousRoute: routes.movingAddAccountDetails,
        route: routes.movingReviewNewHome,
        Component: Screens.ReviewNewHome,
      },
      on: {
        [events.previous]: states.addMovingDetails,
      },
      states: {
        loadNewPlanAmountCents: {
          invoke: {
            src: 'getNewPlanAmountCents',
            onDone: {
              actions: 'setNewPlanAmountCents',
              target: 'newPlanAmountCentsLoaded',
            },
            onError: {
              target: 'newPlanAmountCentsErrored',
            },
          },
        },
        newPlanAmountCentsLoaded: {
          initial: 'idle',
          states: {
            idle: {
              on: {
                [events.next]: 'invokeCreatePlan',
              },
            },
            invokeCreatePlan: {
              invoke: {
                src: 'createPlan',
                onDone: {
                  actions: [
                    'setNewPlanAttributes',
                    'trackPlanAttributesCreated',
                  ],
                  target: 'invokeUpdateMoveRequest',
                },
                onError: { target: 'createPlanError' },
              },
            },
            invokeUpdateMoveRequest: {
              invoke: {
                src: 'createOrUpdateMoveRequest',
                onDone: [
                  {
                    cond: 'isWaitlistUtility',
                    actions: 'trackMoveRequestUpdated',
                    target: '#connectUtility',
                  },
                  {
                    target: '#viewSuccess',
                  },
                ],
              },
            },
            createPlanError: {
              on: { [events.next]: 'invokeCreatePlan' },
            },
          },
        },
        newPlanAmountCentsErrored: {
          type: 'final',
        },
      },
    },
    [states.connectUtility]: {
      id: 'connectUtility',
      meta: {
        previousRoute: routes.movingReviewNewHome,
        route: routes.movingConnectUtility,
        Component: Screens.ConnectUtility,
      },
      on: {
        [events.previous]: states.reviewNewHome,
      },
    },
    [states.viewSuccess]: {
      id: 'viewSuccess',
      type: 'final',
      meta: {
        previousRoute: routes.movingConnectUtility,
        route: routes.movingViewSuccess,
        Component: Screens.ViewSuccess,
      },
      on: {
        [events.previous]: states.reviewNewHome,
      },
    },
  },
};
export const createStateMachine = (apolloClient, userId, accountId) => {
  const apolloServiceHandler = new ApolloServiceHandler(
    apolloClient,
    userId,
    accountId
  );
  const services = {
    createOrUpdateMoveRequest: apolloServiceHandler.createOrUpdateMoveRequest,
    getAccountInfo: apolloServiceHandler.getAccountInfo,
    getNewPlanAmountCents: apolloServiceHandler.getNewPlanAmountCents,
    createPlan: apolloServiceHandler.createPlan,
    getPlanTemplateData: apolloServiceHandler.getPlanTemplateData,
    createSetupIntent: apolloServiceHandler.createSetupIntent,
    pollForPaymentCreation: apolloServiceHandler.pollForPaymentCreation,
    fetchExistingPaymentMethodIds:
      apolloServiceHandler.fetchExistingPaymentMethodIds,
    fetchUtilityAccountsBillingExperience:
      apolloServiceHandler.fetchUtilityAccountsBillingExperience,
    fetchBillingExperienceOptions:
      apolloServiceHandler.fetchBillingExperienceOptions,
  };

  const addOns = { actions, guards, services };
  return createMachine(config, addOns);
};
